// frontend/src/components/staff/PromoCreateModal.js
//
// PROMO-RF-F — Staff Promo Code Management: CREATE form modal (FE).
// Mounts behind the "New promo code" button that RF-F adds into the
// StaffPromoManagementPage `page-actions-slot` seam RF-E reserved.
//
// Story:    docs/backlog/PROMO-RF-F-staff-promo-create-and-archive-form.md
// ADR:      docs/architecture/ADR-029-promo-reframe-reconcile.md
//             D2 — customer discount math STAYS on Stripe-hosted Checkout.
//                  This form only TELLS Stripe what code to create; it does
//                  NO in-app discount math (cited; NOT re-opened).
//             D4 — the in-house /api/promo-codes engine is RF-H's removal.
//                  This form does a NON-authoritative thing: it calls the
//                  RF-B Stripe pass-through ONLY. ZERO in-house PromoCode
//                  write (AC Scenario 5; MSG-201 Q1=A).
//             D5 — consumes the RF-B create contract VERBATIM (←RF-B/#181).
//
// RF-B create contract (PR #181 — read VERBATIM from
//   backend/controllers/stripePromoConsoleController.js +
//   backend/services/stripe/promoCodeService.js):
//     POST /api/staff/promo-codes
//       body { code, discountType: "percentage"|"amount",
//              discountValue (percent 1..100 OR amount in cents),
//              name?, expiresAt? (unix s OR ISO/date string),
//              maxRedemptions?, minPurchaseAmountCents?, metadata? }
//       201 -> { success: true, data: <shaped> }   (shaped = RF-E shape)
//       4xx -> { success: false, error, reason }    (ADR-029 D3)
//   A duplicate code at Stripe arrives as reason "invalid_request"
//   (StripeInvalidRequestError -> HTTP 400) with a BE-provided non-raw
//   `error` string. An upstream Stripe outage arrives as reason
//   "vendor_unavailable" (HTTP 503, retryable).
//
// Scope (AC Scenarios 1, 2, 4, 5):
//   Sc.1 — submit a valid form -> RF-B create -> parent optimistically
//          refreshes the list + shows a success confirmation.
//   Sc.2 — client-side validation blocks an invalid submit with inline
//          field errors; NO create request is sent.
//   Sc.4 — a server-side create error is surfaced non-raw and the form
//          RETAINS the entered values for correction.
//   Sc.5 — NO in-house authoritative write, NO legacy-history affordance.
//
// Production-safety: pure-additive new component. ZERO edits to booking /
//   auth / payment flows; the only mutation is a Stripe pass-through the
//   RF-B story already shipped + deployed. Single-commit revert clean.
//   NOT a Customer-Facing Gate (R-020) — staff surface; Stripe write is
//   synchronous and staff-initiated; no async vendor signal flips any
//   customer-visible state (ADR-029: "NOT a customer-facing gate").

import React from "react";
import "./PromoCreateModal.css";

const STUDIO_TZ_LABEL = "MST";

// All user-facing strings live in the component (code-convention). The
// validator returns stable keys; copy is rendered from FIELD_ERROR_COPY so
// the messages are testable + centralized without leaking strings into
// logic files.
const FIELD_ERROR_COPY = Object.freeze({
  code_required: "Promo code is required.",
  code_format:
    "Use letters, numbers, hyphens or underscores only (no spaces).",
  discountValue_required: "Discount value is required.",
  discountValue_percent_range:
    "Percent discount must be between 1 and 100.",
  discountValue_amount_positive:
    "Amount discount must be greater than $0.00.",
  maxRedemptions_positive:
    "Max redemptions must be a whole number greater than 0.",
  minPurchase_positive: "Minimum purchase must be greater than $0.00.",
  expiresAt_invalid: "Enter a valid expiry date.",
  expiresAt_past: "Expiry must be in the future.",
});

// Stripe promotion-code constraint (Stripe rejects spaces / most symbols).
// We mirror Stripe's accepted character class client-side so the duplicate
// path (Scenario 4) is the only server round-trip a well-formed code makes.
const CODE_RE = /^[A-Za-z0-9_-]+$/;

/**
 * Pure, deterministic client-side validator (AC Scenario 2). Returns a map
 * of fieldName -> error-copy-key for every invalid field; an empty object
 * means the form is submittable. NO request is implied by this function —
 * the caller blocks submit when the returned object is non-empty.
 *
 * @param {Object} form - raw form state (strings as entered)
 * @param {string} form.code
 * @param {"percentage"|"amount"} form.discountType
 * @param {string} form.discountValue - as typed (percent, or dollars)
 * @param {string} [form.maxRedemptions]
 * @param {string} [form.minPurchase] - dollars as typed
 * @param {string} [form.expiresAt] - yyyy-mm-dd as typed
 * @param {number} [nowMs] - injectable clock for deterministic tests
 * @returns {Object<string,string>} fieldName -> FIELD_ERROR_COPY key
 */
export function validatePromoForm(form = {}, nowMs = Date.now()) {
  const errors = {};
  const code = (form.code || "").trim();
  if (code.length === 0) {
    errors.code = "code_required";
  } else if (!CODE_RE.test(code)) {
    errors.code = "code_format";
  }

  const rawValue = (form.discountValue ?? "").toString().trim();
  if (rawValue.length === 0) {
    errors.discountValue = "discountValue_required";
  } else {
    const num = Number(rawValue);
    if (!Number.isFinite(num)) {
      errors.discountValue =
        form.discountType === "amount"
          ? "discountValue_amount_positive"
          : "discountValue_percent_range";
    } else if (form.discountType === "percentage") {
      if (num < 1 || num > 100) {
        errors.discountValue = "discountValue_percent_range";
      }
    } else if (num <= 0) {
      errors.discountValue = "discountValue_amount_positive";
    }
  }

  const maxR = (form.maxRedemptions ?? "").toString().trim();
  if (maxR.length > 0) {
    const n = Number(maxR);
    if (!Number.isInteger(n) || n <= 0) {
      errors.maxRedemptions = "maxRedemptions_positive";
    }
  }

  const minP = (form.minPurchase ?? "").toString().trim();
  if (minP.length > 0) {
    const n = Number(minP);
    if (!Number.isFinite(n) || n <= 0) {
      errors.minPurchase = "minPurchase_positive";
    }
  }

  const exp = (form.expiresAt ?? "").toString().trim();
  if (exp.length > 0) {
    const t = new Date(exp).getTime();
    if (!Number.isFinite(t)) {
      errors.expiresAt = "expiresAt_invalid";
    } else if (t <= nowMs) {
      errors.expiresAt = "expiresAt_past";
    }
  }

  return errors;
}

/**
 * Build the exact RF-B create request body from validated form state.
 * Kept pure + exported so the create contract mapping is unit-testable
 * independent of the React tree. Only includes optional fields when set so
 * the thin pass-through forwards a clean Stripe request (RF-B service
 * skips falsy optionals anyway — we mirror that to keep the body minimal).
 *
 * @param {Object} form - validated form state
 * @returns {Object} RF-B POST /api/staff/promo-codes body
 */
export function buildCreatePayload(form = {}) {
  const code = (form.code || "").trim().toUpperCase();
  const discountType =
    form.discountType === "amount" ? "amount" : "percentage";
  const rawValue = Number((form.discountValue ?? "").toString().trim());

  const payload = {
    code,
    discountType,
    // percentage -> percent number; amount -> integer cents (RF-B service
    // forwards `amount_off` in cents verbatim, currency defaulted usd BE-side).
    discountValue:
      discountType === "amount"
        ? Math.round(rawValue * 100)
        : rawValue,
  };

  const name = (form.name || "").trim();
  if (name.length > 0) {
    payload.name = name;
  }

  const exp = (form.expiresAt ?? "").toString().trim();
  if (exp.length > 0) {
    // RF-B accepts unix seconds OR an ISO/date string; send ISO so the BE
    // does the tz-safe Math.floor(Date.parse/1000). Anchor to end-of-day so
    // a date-only entry stays valid through that whole day.
    payload.expiresAt = new Date(`${exp}T23:59:59`).toISOString();
  }

  const maxR = (form.maxRedemptions ?? "").toString().trim();
  if (maxR.length > 0) {
    payload.maxRedemptions = Number(maxR);
  }

  const minP = (form.minPurchase ?? "").toString().trim();
  if (minP.length > 0) {
    payload.minPurchaseAmountCents = Math.round(Number(minP) * 100);
  }

  return payload;
}

/**
 * Translate an axios error from the RF-B create endpoint into a clear,
 * NON-raw user-facing message (AC Scenario 4). 503/vendor_unavailable is
 * the retryable message; a duplicate code (reason invalid_request /
 * StripeInvalidRequestError) surfaces the BE-provided `error` string; a
 * generic fallback never exposes a raw object/stack.
 * @param {Error} err - axios error
 */
export function messageForCreateError(err) {
  const data = err?.response?.data;
  const reason = data?.reason;
  const status = err?.response?.status;
  if (reason === "vendor_unavailable" || status === 503) {
    return "Stripe is temporarily unavailable. Your entries were kept — please try again in a moment.";
  }
  return (
    data?.error ||
    err?.userMessage ||
    "Couldn't create the promo code. Please review the form and try again."
  );
}

const INITIAL_FORM = Object.freeze({
  code: "",
  name: "",
  discountType: "percentage",
  discountValue: "",
  maxRedemptions: "",
  minPurchase: "",
  expiresAt: "",
});

const PromoCreateModal = ({ open, onClose, onCreated }) => {
  const [form, setForm] = React.useState(INITIAL_FORM);
  const [errors, setErrors] = React.useState({});
  const [submitting, setSubmitting] = React.useState(false);
  const [serverError, setServerError] = React.useState(null);
  const firstFieldRef = React.useRef(null);

  // Fresh state on each open (no stale values from a prior session).
  React.useEffect(() => {
    if (open) {
      setForm(INITIAL_FORM);
      setErrors({});
      setSubmitting(false);
      setServerError(null);
    }
  }, [open]);

  // Esc closes (a11y parity with the other staff modals) unless mid-submit.
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !submitting) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, submitting]);

  if (!open) return null;

  const setField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    // Clear that field's inline error as the staffer corrects it.
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleSubmit = async (e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    if (submitting) return;

    // AC Scenario 2 — client-side validation BLOCKS submit; NO request.
    const found = validatePromoForm(form);
    if (Object.keys(found).length > 0) {
      setErrors(found);
      setServerError(null);
      return;
    }

    setErrors({});
    setServerError(null);
    setSubmitting(true);
    try {
      const payload = buildCreatePayload(form);
      // Lazy import keeps the create call inside the service layer
      // (no inline axios in components) without import-cycle risk.
      const { staffPromoAPI } = require("../../services/api");
      const res = await staffPromoAPI.create(payload);
      const created = res?.data?.data || null;
      // AC Scenario 5 — NO in-house write here. The ONLY mutation is the
      // RF-B Stripe pass-through above. Parent owns the optimistic list
      // update + success confirmation (AC Scenario 1).
      if (typeof onCreated === "function") onCreated(created);
    } catch (err) {
      // AC Scenario 4 — non-raw message; form values RETAINED (we never
      // reset `form` on error).
      setServerError(messageForCreateError(err));
      setSubmitting(false);
    }
  };

  const errMsg = (field) =>
    errors[field] ? FIELD_ERROR_COPY[errors[field]] : null;

  const isAmount = form.discountType === "amount";

  return (
    <>
      <div
        className="promo-create-modal__backdrop"
        onClick={submitting ? undefined : onClose}
        aria-hidden="true"
        data-testid="promo-create-modal-backdrop"
      />
      <div
        className="promo-create-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="promo-create-modal-heading"
        data-testid="promo-create-modal"
      >
        <header className="promo-create-modal__header">
          <h2
            id="promo-create-modal-heading"
            className="promo-create-modal__heading"
          >
            New promo code
          </h2>
          <button
            type="button"
            className="promo-create-modal__close"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close new promo code form"
            data-testid="promo-create-modal-close"
          >
            &times;
          </button>
        </header>

        <form className="promo-create-modal__body" onSubmit={handleSubmit}>
          {serverError && (
            <div
              className="promo-create-modal__server-error"
              role="alert"
              data-testid="promo-create-modal-server-error"
            >
              {serverError}
            </div>
          )}

          <p className="promo-create-modal__hint">
            Stripe is the source of truth — this creates the code in Stripe.
            Customer discounts apply at Stripe checkout.
          </p>

          <label
            className="promo-create-modal__label"
            htmlFor="promo-create-code"
          >
            Code
            <span className="promo-create-modal__required" aria-hidden="true">
              {" "}
              *
            </span>
          </label>
          <input
            id="promo-create-code"
            ref={firstFieldRef}
            className="promo-create-modal__input promo-create-modal__input--code"
            type="text"
            value={form.code}
            onChange={(e) => setField("code", e.target.value)}
            disabled={submitting}
            autoComplete="off"
            aria-required="true"
            aria-invalid={errors.code ? "true" : "false"}
            aria-describedby="promo-create-code-err"
            placeholder="SUMMER20"
            data-testid="promo-create-code"
          />
          {errMsg("code") && (
            <p
              id="promo-create-code-err"
              className="promo-create-modal__field-error"
              data-testid="promo-create-code-err"
            >
              {errMsg("code")}
            </p>
          )}

          <label
            className="promo-create-modal__label"
            htmlFor="promo-create-name"
          >
            Display name <span className="promo-create-modal__optional">(optional)</span>
          </label>
          <input
            id="promo-create-name"
            className="promo-create-modal__input"
            type="text"
            value={form.name}
            onChange={(e) => setField("name", e.target.value)}
            disabled={submitting}
            autoComplete="off"
            placeholder="Summer 20% off"
            data-testid="promo-create-name"
          />

          <div className="promo-create-modal__row">
            <div className="promo-create-modal__col">
              <label
                className="promo-create-modal__label"
                htmlFor="promo-create-discount-type"
              >
                Discount type
                <span
                  className="promo-create-modal__required"
                  aria-hidden="true"
                >
                  {" "}
                  *
                </span>
              </label>
              <select
                id="promo-create-discount-type"
                className="promo-create-modal__input"
                value={form.discountType}
                onChange={(e) => setField("discountType", e.target.value)}
                disabled={submitting}
                data-testid="promo-create-discount-type"
              >
                <option value="percentage">Percent off</option>
                <option value="amount">Amount off (USD)</option>
              </select>
            </div>

            <div className="promo-create-modal__col">
              <label
                className="promo-create-modal__label"
                htmlFor="promo-create-discount-value"
              >
                {isAmount ? "Amount off ($)" : "Percent off (%)"}
                <span
                  className="promo-create-modal__required"
                  aria-hidden="true"
                >
                  {" "}
                  *
                </span>
              </label>
              <input
                id="promo-create-discount-value"
                className="promo-create-modal__input"
                type="number"
                inputMode="decimal"
                min={isAmount ? "0.01" : "1"}
                max={isAmount ? undefined : "100"}
                step={isAmount ? "0.01" : "1"}
                value={form.discountValue}
                onChange={(e) =>
                  setField("discountValue", e.target.value)
                }
                disabled={submitting}
                aria-required="true"
                aria-invalid={errors.discountValue ? "true" : "false"}
                aria-describedby="promo-create-discount-value-err"
                placeholder={isAmount ? "15.00" : "20"}
                data-testid="promo-create-discount-value"
              />
              {errMsg("discountValue") && (
                <p
                  id="promo-create-discount-value-err"
                  className="promo-create-modal__field-error"
                  data-testid="promo-create-discount-value-err"
                >
                  {errMsg("discountValue")}
                </p>
              )}
            </div>
          </div>

          <div className="promo-create-modal__row">
            <div className="promo-create-modal__col">
              <label
                className="promo-create-modal__label"
                htmlFor="promo-create-max-redemptions"
              >
                Max redemptions{" "}
                <span className="promo-create-modal__optional">
                  (optional)
                </span>
              </label>
              <input
                id="promo-create-max-redemptions"
                className="promo-create-modal__input"
                type="number"
                inputMode="numeric"
                min="1"
                step="1"
                value={form.maxRedemptions}
                onChange={(e) =>
                  setField("maxRedemptions", e.target.value)
                }
                disabled={submitting}
                aria-invalid={errors.maxRedemptions ? "true" : "false"}
                aria-describedby="promo-create-max-redemptions-err"
                placeholder="Unlimited"
                data-testid="promo-create-max-redemptions"
              />
              {errMsg("maxRedemptions") && (
                <p
                  id="promo-create-max-redemptions-err"
                  className="promo-create-modal__field-error"
                  data-testid="promo-create-max-redemptions-err"
                >
                  {errMsg("maxRedemptions")}
                </p>
              )}
            </div>

            <div className="promo-create-modal__col">
              <label
                className="promo-create-modal__label"
                htmlFor="promo-create-min-purchase"
              >
                Min purchase ($){" "}
                <span className="promo-create-modal__optional">
                  (optional)
                </span>
              </label>
              <input
                id="promo-create-min-purchase"
                className="promo-create-modal__input"
                type="number"
                inputMode="decimal"
                min="0.01"
                step="0.01"
                value={form.minPurchase}
                onChange={(e) => setField("minPurchase", e.target.value)}
                disabled={submitting}
                aria-invalid={errors.minPurchase ? "true" : "false"}
                aria-describedby="promo-create-min-purchase-err"
                placeholder="No minimum"
                data-testid="promo-create-min-purchase"
              />
              {errMsg("minPurchase") && (
                <p
                  id="promo-create-min-purchase-err"
                  className="promo-create-modal__field-error"
                  data-testid="promo-create-min-purchase-err"
                >
                  {errMsg("minPurchase")}
                </p>
              )}
            </div>
          </div>

          <label
            className="promo-create-modal__label"
            htmlFor="promo-create-expires-at"
          >
            Expiry date{" "}
            <span className="promo-create-modal__optional">(optional)</span>
          </label>
          <input
            id="promo-create-expires-at"
            className="promo-create-modal__input"
            type="date"
            value={form.expiresAt}
            onChange={(e) => setField("expiresAt", e.target.value)}
            disabled={submitting}
            aria-invalid={errors.expiresAt ? "true" : "false"}
            aria-describedby="promo-create-expires-at-err"
            data-testid="promo-create-expires-at"
          />
          <p className="promo-create-modal__helper">
            Codes expire at end of day, {STUDIO_TZ_LABEL}.
          </p>
          {errMsg("expiresAt") && (
            <p
              id="promo-create-expires-at-err"
              className="promo-create-modal__field-error"
              data-testid="promo-create-expires-at-err"
            >
              {errMsg("expiresAt")}
            </p>
          )}

          <footer className="promo-create-modal__footer">
            <button
              type="button"
              className="promo-create-modal__cancel"
              onClick={onClose}
              disabled={submitting}
              data-testid="promo-create-modal-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="promo-create-modal__confirm"
              disabled={submitting}
              data-testid="promo-create-modal-submit"
            >
              {submitting ? "Creating…" : "Create promo code"}
            </button>
          </footer>
        </form>
      </div>
    </>
  );
};

export default PromoCreateModal;
